import React, { useState, useMemo } from 'react';

type TabProps = {
  title: string;
  children: React.ReactNode;
  icon: string;
};

type TabsProps = {
  children: React.ReactElement<TabProps>[] | React.ReactElement<TabProps>;
  initialActiveTab?: number;
  /**
   * Минимальная высота области контента, чтобы при смене таба
   * строка-родитель не «дышала». По умолчанию 240px — этого хватает
   * на 2-строчный grid контактов / комментарий, а длинные табы
   * (доставка, товары) спокойно перерастают min-h по обычной логике.
   */
  contentMinHeight?: number;
};

export const TabsWithIcons: React.FC<TabsProps> = ({
  children,
  initialActiveTab = 0,
  contentMinHeight = 240,
}) => {
  const [activeTab, setActiveTab] = useState<number>(initialActiveTab);

  const filteredChildren = useMemo(
    () =>
      React.Children.toArray(children).filter(
        (child: any) => child.props.title
      ),
    [children]
  );

  return (
    <div className="w-full bg-white overflow-hidden">
      {/* Тулбар табов: компактнее, ровный ряд, белый фон вписывается
          в новую серую подложку OrderCardExpanded. */}
      <ul className="flex flex-wrap gap-1 list-none m-0 px-2 pt-2 pb-0 bg-white border-b border-gray-100">
        {filteredChildren.map((child: any, index) => {
          const isActive = activeTab === index;
          return (
            <li
              key={index}
              className={`relative flex gap-2 items-center py-2 px-3 cursor-pointer text-sm font-semibold rounded-md transition-colors
                after:content-[''] after:absolute after:bottom-[-1px] after:left-3 after:right-3 after:h-[2px]
                after:transition-opacity after:duration-150 after:rounded-t
                ${
                  isActive
                    ? 'text-[#242426] after:bg-[#e3282a] after:opacity-100'
                    : 'text-[#9b9fac] hover:text-[#5b5e69] after:opacity-0'
                }`}
              onClick={() => setActiveTab(index)}
            >
              <img
                src={child.props.icon}
                alt=""
                aria-hidden
                className={`w-5 h-5 ${
                  isActive
                    ? '[filter:invert(37%)_sepia(93%)_saturate(7471%)_hue-rotate(356deg)_brightness(91%)_contrast(135%)]'
                    : 'opacity-60'
                }`}
              />
              {child.props.title}
            </li>
          );
        })}
      </ul>

      {/*
        КЛЮЧЕВОЕ ИСПРАВЛЕНИЕ: фиксированный min-height на области
        контента. До этого высота менялась от таба к табу (товары —
        длинная таблица, комментарий — одна строка), и при клике
        строка-родитель в DataTable «дёргалась». Теперь min-h держит
        нижнюю границу: короткие табы не сжимают строку обратно.
      */}
      <div
        className="px-4 py-4"
        style={{ minHeight: contentMinHeight }}
      >
        {filteredChildren.map((child: any, index) =>
          activeTab === index ? (
            <div key={index}>{child.props.children}</div>
          ) : null
        )}
      </div>
    </div>
  );
};
